import { z } from "zod";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * Public IPs (Hetzner "Primary IPs"): addresses that belong to the project,
 * can be moved between servers of one region and outlive a server unless
 * `autoDelete` is on. Floating IPs are a separate kind (phase 2).
 */

export const IpFamily = z.enum(["ipv4", "ipv6"]);
export type IpFamily = z.infer<typeof IpFamily>;

export const PublicIpName = z.string().trim().min(1).max(128);

/** A hostname for reverse DNS; null puts back the provider's default. */
export const DNS_NAME_PATTERN =
  /^(?=.{1,253}$)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}\.?$/;

export const PublicIp = z.object({
  id: z.uuid(),
  name: z.string(),
  family: IpFamily,
  /** The address; for IPv6 the routed /64 network, e.g. `2001:db8:1::/64`. */
  address: z.string(),
  region: z.string(),
  zone: z.string(),
  /** The server it is assigned to, null when unassigned (filled in by the API). */
  server: z.object({ id: z.uuid().nullable(), name: z.string() }).nullable(),
  /** The provider's id of that server, as stored in the resource copy. */
  assigneeRef: z.string().nullable(),
  /** Reverse DNS entries: one for IPv4, any addresses of the /64 for IPv6. */
  reverseDns: z.array(z.object({ ip: z.string(), dnsPtr: z.string() })),
  /** Deleted together with the server it is assigned to. */
  autoDelete: z.boolean(),
  /** Blocked by the provider (abuse); cannot be used until unblocked. */
  blocked: z.boolean(),
  protection: z.object({ delete: z.boolean() }),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type PublicIp = z.infer<typeof PublicIp>;

export const CreatePublicIpRequest = z
  .object({
    name: PublicIpName,
    family: IpFamily,
    /** Where the address lives; taken from the server when `serverId` is given. */
    region: z.string().min(1).max(64).optional(),
    serverId: z.uuid().optional(),
    autoDelete: z.boolean().default(false),
    labels: Labels.default({}),
  })
  .refine((v) => v.region !== undefined || v.serverId !== undefined, {
    message: "Choose a region or a server",
    path: ["region"],
  });
export type CreatePublicIpRequest = z.infer<typeof CreatePublicIpRequest>;

export const UpdatePublicIpRequest = z
  .object({
    name: PublicIpName.optional(),
    autoDelete: z.boolean().optional(),
    labels: Labels.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to change");
export type UpdatePublicIpRequest = z.infer<typeof UpdatePublicIpRequest>;

export const AssignPublicIpRequest = z.object({ serverId: z.uuid() });

export const ReverseDnsRequest = z.object({
  /** The IPv4 address itself, or one address inside the IPv6 /64. */
  ip: z.string().min(2).max(45),
  dnsPtr: z.string().trim().regex(DNS_NAME_PATTERN, "Not a hostname").nullable(),
});
export type ReverseDnsRequest = z.infer<typeof ReverseDnsRequest>;

export const PublicIpProtectionRequest = z.object({ delete: z.boolean() });

/** Answer of writes the provider finishes asynchronously. */
export const PublicIpChange = z.object({ publicIp: PublicIp, operation: Operation });
export type PublicIpChange = z.infer<typeof PublicIpChange>;
